/** @jsxImportSource @emotion/react */
import { Button, Tab, Tabs } from "@blueprintjs/core"
import { IconNames } from "@blueprintjs/icons"
import { SetStateAction, Dispatch, useEffect, useState } from "react"

import CreateShortLocateForm, { ShortLocateForm } from "./components/CreateShortLocateForm"
import ShortLocatesTable from "./components/ShortLocatesTable"
import useGetShortLocates from "./hooks/useGetShortLocates"
import { ShortLocateDialog, TabsContainer } from "./ManualShortLocateModal.styled"
import { FooterActions } from "./ReplaceOrderModal.styled"

interface ManualShortLocateModalProps {
  isOpen: boolean
  onClose: () => void
  locateInstrumentQuery?: string
  locateQuantity?: number
}

enum LocateTab {
  CREATE = "create",
  VIEW = "view",
}

const ManualShortLocateModal = ({
  isOpen,
  onClose,
  locateInstrumentQuery,
  locateQuantity,
}: ManualShortLocateModalProps) => {
  const [activeTab, setActiveTab] = useState<LocateTab>(LocateTab.CREATE)

  // Get existing locates
  const {
    locates,
    isLoading: isLoadingLocates,
    refreshLocates,
  } = useGetShortLocates({
    enabled: isOpen && activeTab === LocateTab.VIEW,
  })

  // Fetch locates when modal opens or tab changes to VIEW
  useEffect(() => {
    if (isOpen && activeTab === LocateTab.VIEW) {
      refreshLocates()
    }
  }, [isOpen, activeTab, refreshLocates])

  const handleTabChange = (newTabId: string) => {
    setActiveTab(newTabId as LocateTab)
    if (newTabId === LocateTab.VIEW) {
      refreshLocates()
    }
  }

  const handleLocateCreationSuccess = () => {
    // Switch to view tab after successful creation to see the new locate
    setActiveTab(LocateTab.VIEW)
    refreshLocates()
  }

  const handleLocateViewClose = () => {
    setActiveTab(LocateTab.CREATE)
    onClose()
  }

  return (
    <ShortLocateDialog
      isOpen={isOpen}
      onClose={handleLocateViewClose}
      title="Locates"
      className="bp5-dark"
    >
      <div className="bp3-dialog-body">
        <TabsContainer>
          <Tabs
            id="locate-tabs"
            selectedTabId={activeTab}
            onChange={handleTabChange}
            animate={false}
          >
            <Tab id={LocateTab.CREATE} title="Create New" />
            <Tab id={LocateTab.VIEW} title="View Locates" />
          </Tabs>
        </TabsContainer>

        {activeTab === LocateTab.CREATE ? (
          <CreateShortLocateForm
            locateInstrumentQuery={locateInstrumentQuery}
            locateQuantity={locateQuantity}
            onSuccess={handleLocateCreationSuccess}
            onCancel={onClose}
          />
        ) : (
          <>
            <ShortLocatesTable
              locates={locates}
              isLoading={isLoadingLocates}
              onRefresh={refreshLocates}
            />
            <div className="bp3-dialog-footer">
              <FooterActions>
                <Button onClick={onClose} icon={IconNames.CROSS}>
                  Close
                </Button>
              </FooterActions>
            </div>
          </>
        )}
      </div>
    </ShortLocateDialog>
  )
}

export default ManualShortLocateModal
